"use client";
import { useState, useCallback, useMemo, useEffect } from "react";

export interface Invoice {
  id: string;
  number: string;
  vendor: string;
  amount: number;
  status: "pending" | "approved" | "rejected" | "paid";
  dueDate: string;
  category: string;
}

// Mock data generation
const vendors = [
  "Acme Corp",
  "GlobalTech",
  "Pinnacle Services",
  "Summit Solutions",
  "BlueStar Ltd",
  "NexGen Inc",
  "CoreFlow",
  "DataWise",
  "TechPrime",
  "CloudBase",
];
const categories = [
  "Operations",
  "Marketing",
  "IT",
  "HR",
  "Legal",
  "Facilities",
  "R&D",
  "Finance",
];
const statuses: Invoice["status"][] = [
  "pending",
  "approved",
  "rejected",
  "paid",
];

const generateMockInvoices = (): Invoice[] => {
  return Array.from({ length: 50 }, (_, i) => ({
    id: `inv-${i + 1}`,
    number: `INV-2024-${String(i + 1001).padStart(4, "0")}`,
    vendor: vendors[i % vendors.length],
    amount: Math.round((Math.random() * 50000 + 500) * 100) / 100,
    status: statuses[Math.floor(Math.random() * statuses.length)],
    dueDate: new Date(
      2024,
      Math.floor(Math.random() * 12),
      Math.floor(Math.random() * 28) + 1
    )
      .toISOString()
      .split("T")[0],
    category: categories[Math.floor(Math.random() * categories.length)],
  }));
};

export interface ListingController {
  /** All invoices (unfiltered) — pass to DataTable for full client-side handling */
  allInvoices: Invoice[];
  rows: Invoice[];
  totalRows: number;
  loading: boolean;
  search: string;
  setSearch: (s: string) => void;
  filters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  page: number;
  pageSize: number;
  totalPages: number;
  setPage: (p: number) => void;
  setPageSize: (s: number) => void;
  sortField: string | null;
  sortDir: "asc" | "desc";
  setSort: (field: string) => void;
  selectedIds: Set<string>;
  toggleSelect: (id: string) => void;
  toggleSelectAll: () => void;
  clearSelection: () => void;
  bulkAction: (action: string) => void;
  refresh: () => void;
}

export function useListingController(): ListingController {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);

  // Initialize mock data on mount
  useEffect(() => {
    setAllInvoices(generateMockInvoices());
  }, []);

  // Filter invoices by search and filters
  const filteredInvoices = useMemo(() => {
    let result = [...allInvoices];

    // Search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.number.toLowerCase().includes(searchLower) ||
          inv.vendor.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status) {
      result = result.filter((inv) => inv.status === filters.status);
    }

    // Category filter
    if (filters.category) {
      result = result.filter((inv) => inv.category === filters.category);
    }

    return result;
  }, [allInvoices, search, filters]);

  // Sort invoices
  const sortedInvoices = useMemo(() => {
    if (!sortField) return filteredInvoices;

    return [...filteredInvoices].sort((a, b) => {
      const aVal = a[sortField as keyof Invoice];
      const bVal = b[sortField as keyof Invoice];

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      const cmp = aStr.localeCompare(bStr);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredInvoices, sortField, sortDir]);

  // Paginate
  const totalRows = sortedInvoices.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const startIdx = (page - 1) * pageSize;
  const rows = sortedInvoices.slice(startIdx, startIdx + pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [search, filters]);

  const setFilter = useCallback((key: string, value: string) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (value === "") {
        delete updated[key];
      } else {
        updated[key] = value;
      }
      return updated;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearch("");
  }, []);

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allPageIds = new Set(rows.map((r) => r.id));
      if (prev.size === rows.length && rows.every((r) => prev.has(r.id))) {
        return new Set();
      }
      return new Set([...prev, ...allPageIds]);
    });
  }, [rows]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const bulkAction = useCallback((action: string) => {
    const ids = Array.from(selectedIds);
    console.log(`Bulk action: ${action} on invoices:`, ids);
    // In a real app, this would make an API call
    clearSelection();
  }, [selectedIds, clearSelection]);

  const refresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setAllInvoices(generateMockInvoices());
      setLoading(false);
    }, 500);
  }, []);

  return {
    allInvoices,
    rows,
    totalRows,
    loading,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    page,
    pageSize,
    totalPages,
    setPage,
    setPageSize,
    sortField,
    sortDir,
    setSort: toggleSort,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    bulkAction,
    refresh,
  };
}
